/* eslint-disable no-nested-ternary */
import { FormInstance } from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import produce from 'immer';
import AutoCategory from 'model/ServiceRequests/AutoCategory';
import Category from 'model/ServiceRequests/Category';
import { ICustomerAccount } from 'model/ServiceRequests/CustomerAccount';
import SubCategory from 'model/ServiceRequests/SubCategory';
import {
  IAttachments,
  ICreateServiceRequestParams,
  IFetchParams,
  IServiceRequestResponse,
  checkIfDeskManagerUserIsValid,
  createServiceRequest,
  fetchCustomerAccounts,
  getServiceRequestsAutoCategories,
  getServiceRequestsCategories,
  getServiceRequestsSubCategories
} from 'services/serviceRequestsService';
import { create } from 'zustand';

export type IActions = {
  setData: (fn: (state: IServiceRequests) => void) => void;
  setFormInstance: (form: Partial<FormInstance<IFormFilter>>) => void;
  getFilters: () => Promise<void>;
  getCategories: () => void;
  getSubCategories: () => void;
  getAutoCategories: () => void;
  getCustomerAccounts: () => void;
  isEmailValid: (email: string) => Promise<boolean>;
  validateEmailAndLoadFilters: (email: string) => void;
  sendAttachmentFile: (file: UploadChangeParam<any>) => void;
  createServiceRequest: () => Promise<IServiceRequestResponse>;
  resetFilters: () => void;
  clearAttachmentFile: () => void;
};

export type ISelectors = {
  isFiltersSuccessful: () => boolean;
};

export type IGetters = {
  getAttachments(): IAttachments[];
}

export type IFormFilter = {
  customerAccountCode: number | null;
  categoryId: number | null;
  subCategoryId: number | null;
  autoCategoryId: number | null;
  description: string | null;
  menuOrPage: string | null;
  completeName: string | null;
  periodDate: any[];
  invoiceIssuanceLocation: string | null;
  saleType: string | null;
  orderNumber: string | null;
  allProductsReceived: string | null;
  allProductsDeliveredToBox: string | null;
  hasOrderProductDiscrepancy: string | null;
  discrepancyDescription: string | null;
  responsibleNameWhenInvoiceIssuance: string | null;
  siteKeyWhenInvoiceRefused: string | null;
  invoiceNumber: string | null;
  refusalReason: string | null;
  billingStore: string | null;
  responsibleNameWhenInvoiceRefused: string | null;
  siteKeyWhenReturnWithInvoice: string | null;
  customerReturnInvoiceNumber: string | null;
  customerReturnInvoiceValue: string | null;
  returnType: string | null;
  returnReason: string | null;
  physicalProductReturned: string | null;
  returnLocation: string | null;
  invoiceIssuingStore: string | null;
  logisticOperatorResponsibleName: string | null;
  cvhInvoiceNumber: string | null;
  cvhInvoiceValue: string | null;
};

export type IFilters = {
  categories: {
    data: Category[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  subCategories: {
    data: SubCategory[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
  };
  autoCategories: {
    data: AutoCategory[];
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    errorMessage: string;
    isFailure: boolean;
    isReports: boolean;
    isInvoiceInssuance: boolean;
    isInvoiceRefused: boolean;
    isReturnWithInvoice: boolean;
  };
  userInformation: {
    data: ICustomerAccount[] | null;
    isLoading: boolean;
    isSuccessful: boolean;
    isError: boolean;
    isEletronicDistribution: boolean;
    isCarrefour: boolean;
  };
  attachments: {
    data: any[];
    info: IAttachments[];
    isLoading: boolean;
  };
  email: {
    data: string;
    isValid: boolean;
  };

  formFilter: IFormFilter;
};

export type IServiceRequests = {
  filters: IFilters;
  formFilterInstance: Partial<FormInstance<IFormFilter>> | null;
  buttonDisabled: boolean;
} & IActions &
  ISelectors &
  IGetters;

export const initialStateFilters: IFilters = {
  categories: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
  },
  subCategories: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
  },
  autoCategories: {
    data: [],
    isLoading: false,
    isSuccessful: false,
    isError: false,
    errorMessage: '',
    isFailure: false,
    isReports: false,
    isInvoiceInssuance: false,
    isInvoiceRefused: false,
    isReturnWithInvoice: false,
  },
  userInformation: {
    data: null,
    isLoading: false,
    isSuccessful: false,
    isError: false,
    isEletronicDistribution: false,
    isCarrefour: false,
  },
  attachments: {
    data: [],
    info: [],
    isLoading: false,
  },
  email: {
    data: '',
    isValid: false,
  },

  formFilter: {
    customerAccountCode: null,
    categoryId: null,
    subCategoryId: null,
    autoCategoryId: null,
    description: null,
    menuOrPage: null,
    completeName: null,
    periodDate: [],
    invoiceIssuanceLocation: null,
    saleType: null,
    orderNumber: null,
    allProductsReceived: null,
    allProductsDeliveredToBox: null,
    hasOrderProductDiscrepancy: null,
    discrepancyDescription: null,
    responsibleNameWhenInvoiceIssuance: null,
    siteKeyWhenInvoiceRefused: null,
    invoiceNumber: null,
    refusalReason: null,
    billingStore: null,
    responsibleNameWhenInvoiceRefused: null,
    siteKeyWhenReturnWithInvoice: null,
    customerReturnInvoiceNumber: null,
    customerReturnInvoiceValue: null,
    returnType: null,
    returnReason: null,
    physicalProductReturned: null,
    returnLocation: null,
    invoiceIssuingStore: null,
    logisticOperatorResponsibleName: null,
    cvhInvoiceNumber: null,
    cvhInvoiceValue: null,
  },
};

const useServiceRequestsStore = create<IServiceRequests>()((set, get) => ({
  // States
  filters: initialStateFilters,
  formFilterInstance: null,
  buttonDisabled: false,

  // Setters
  setData: (fn) => set(produce(fn)),
  setFormInstance: (form: Partial<FormInstance<IFormFilter>>) => {
    set({ formFilterInstance: form });
  },

  // Selectors
  isFiltersSuccessful: () => {
    const { categories, subCategories, autoCategories } = get().filters;
    return (
      categories.isSuccessful &&
      subCategories.isSuccessful &&
      autoCategories.isSuccessful
    );
  },

  // Getters
  getAttachments: () => get().filters.attachments.info,

  // Actions
  getCategories: async () => {
    const { setData } = get();

    setData((state) => {
      state.filters.categories.isLoading = true;
      state.filters.categories.isSuccessful = false;
      state.filters.categories.isError = false;
    });

    try {
      const params: IFetchParams = {
        Pesquisa: '',
        Ativo: 'S',
        isProducer: false,
      };

      const { data } = await getServiceRequestsCategories(params);

      setData((state) => {
        state.filters.categories.data = data;
        state.filters.categories.isSuccessful = true;
        state.filters.categories.isError = false;
      });
    } catch (error) {
      setData((state) => {
        state.filters.categories.data = [];
        state.filters.categories.isSuccessful = false;
        state.filters.categories.isError = true;
      });
    } finally {
      setData((state) => {
        state.filters.categories.isLoading = false;
      });
    }
  },
  getSubCategories: async () => {
    const { setData, filters } = get();
    const { userInformation } = filters;

    setData((state) => {
      state.filters.subCategories.isLoading = true;
      state.filters.subCategories.isSuccessful = false;
      state.filters.subCategories.isError = false;
    });

    try {
      const categoryIdSelected =
        get().formFilterInstance?.getFieldValue?.('categoryId') ?? null;

      const categoryNameSelected =
        get().filters.categories.data.find(
          (i) => i.account === categoryIdSelected,
        )?.name || '';

      const params: IFetchParams = {
        Pesquisa: categoryNameSelected,
        Ativo: 'S',
        isProducer: false,
      };

      const { data } = await getServiceRequestsSubCategories(params);

      const filteredData = data.filter(subCategory => {
        const name = subCategory.name.toLowerCase();

        const shouldExcludeIsCarrefour =
          !userInformation?.isCarrefour &&
          (name.includes('nota fiscal recusada'.toLowerCase()) ||
            name.includes('devolução com nota fiscal refaturar'.toLowerCase()));

        const shouldExcludeEletronicDistribution =
          !userInformation?.isEletronicDistribution &&
          name.includes('emissão nota fiscal'.toLowerCase());

        return !shouldExcludeIsCarrefour && !shouldExcludeEletronicDistribution;
      });

      setData((state) => {
        state.filters.subCategories.data = filteredData;
        state.filters.subCategories.isSuccessful = true;
        state.filters.subCategories.isError = false;
      });
    } catch (error) {
      setData((state) => {
        state.filters.subCategories.data = [];
        state.filters.subCategories.isSuccessful = false;
        state.filters.subCategories.isError = true;
      });
    } finally {
      setData((state) => {
        state.filters.subCategories.isLoading = false;
      });
    }
  },
  getAutoCategories: async () => {
    const { setData, filters } = get();
    const { userInformation } = filters;

    setData((state) => {
      state.filters.autoCategories.isLoading = true;
      state.filters.autoCategories.isSuccessful = false;
      state.filters.autoCategories.isError = false;
    });

    try {
      const categoryIdSelected =
        get().formFilterInstance?.getFieldValue?.('categoryId') ?? null;
      const subCategoryIdSelected =
        get().formFilterInstance?.getFieldValue?.('subCategoryId') ?? null;

      const categoryNameSelected =
        get().filters.categories.data.find(
          (category) => category.account === categoryIdSelected,
        )?.name || '';
      const subCategoryNameSelected =
        get().filters.subCategories.data.find(
          (subCategory) => subCategory.account === subCategoryIdSelected,
        )?.name || '';

      const params: IFetchParams = {
        Pesquisa:
          categoryNameSelected && subCategoryNameSelected
            ? `${categoryNameSelected} - ${subCategoryNameSelected}`
            : categoryNameSelected || subCategoryNameSelected || '',
        Ativo: 'S',
        isProducer: false,
      };

      const { data } = await getServiceRequestsAutoCategories(params);

      const filteredData = data.filter(autoCategory => {
        const name = autoCategory.name.toLowerCase();

        const shouldExcludeIsCarrefour =
          !userInformation?.isCarrefour &&
          (name.includes('nota fiscal recusada'.toLowerCase()) ||
            name.includes('devolução com nota fiscal refaturar'.toLowerCase()));

        const shouldExcludeEletronicDistribution =
          !userInformation?.isEletronicDistribution &&
          name.includes('emissão nota fiscal'.toLowerCase());

        return !shouldExcludeIsCarrefour && !shouldExcludeEletronicDistribution;
      });

      setData((state) => {
        state.filters.autoCategories.data = filteredData;
        state.filters.autoCategories.isSuccessful = true;
        state.filters.autoCategories.isError = false;
      });
    } catch (error) {
      setData((state) => {
        state.filters.autoCategories.data = [];
        state.filters.autoCategories.isSuccessful = false;
        state.filters.autoCategories.isError = true;
      });
    } finally {
      setData((state) => {
        state.filters.autoCategories.isLoading = false;
      });
    }
  },
  getCustomerAccounts: async () => {
    const { setData } = get();
    setData(state => {
      state.filters.userInformation.isLoading = true;
      state.filters.userInformation.isSuccessful = false;
      state.filters.userInformation.isError = false;
    });
    try {
      const { data } = await fetchCustomerAccounts();
      setData(state => {
        state.filters.userInformation.data = data;
        state.filters.userInformation.isSuccessful = true;
        state.filters.userInformation.isError = false;
      });
    } catch (error) {
      setData(state => {
        state.filters.userInformation.data = null;
        state.filters.userInformation.isSuccessful = false;
        state.filters.userInformation.isError = true;
      });
    } finally {
      setData(state => {
        state.filters.userInformation.isLoading = false;
      });
    }
  },
  getFilters: async () => {
    const { getCategories, getSubCategories, getAutoCategories, getCustomerAccounts } = get();
    try {
      await Promise.all([
        getCategories(),
        getSubCategories(),
        getAutoCategories(),
        getCustomerAccounts(),
      ]);
      return Promise.resolve();
    } catch (error) {
      console.error('Error ao carregar filtros.', error);
      return Promise.reject(error);
    }
  },
  resetFilters: () => {
    const { setData } = get();
    setData((state) => {
      state.filters = initialStateFilters;
    });
  },
  isEmailValid: async (email: string) => {
    try {
      const response = await checkIfDeskManagerUserIsValid(email);
      const isUserValid = response.data;

      if (!isUserValid || typeof isUserValid !== 'number') {
        return false;
      }
      return true;
    } catch (error) {
      console.error('Erro ao validar usuário.', error);
      return Promise.reject(error);
    }
  },
  validateEmailAndLoadFilters: async (email: string) => {
    const { setData, isEmailValid, getFilters } = get();
    const userExists = await isEmailValid(email);

    if (userExists) {
      getFilters();
      setData((state) => {
        state.filters.email.data = email;
        state.filters.email.isValid = true;
      });
    } else {
      setData((state) => {
        state.filters.email.isValid = false;
      });
    }
  },
  sendAttachmentFile: async (file: UploadChangeParam<UploadFile>) => {
    const { setData } = get();

    setData(state => {
      state.filters.attachments.data = file.fileList.map(f => ({
        ...f,
        status: 'uploading'
      }));
    });

    try {
      const attachments: IAttachments[] = await Promise.all(
        file.fileList.map(fileItem => {
          const originFile = fileItem.originFileObj;
          return new Promise<IAttachments>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(originFile as Blob);
            reader.onload = () => {
              const base64String = (reader.result as string).split(',')[1];
              resolve({
                base64: base64String,
                name: fileItem.name,
                type: fileItem.type || 'application/octet-stream'
              });
            };
            reader.onerror = error => reject(error);
          });
        })
      );

      setData(state => {
        state.filters.attachments.info = attachments;
      });

      setData(state => {
        state.filters.attachments.data = file.fileList.map(f => ({
          ...f,
          status: 'done'
        }));
      });
    } catch (error) {
      console.error('Erro ao processar anexos', error);

      setData(state => {
        state.filters.attachments.data = file.fileList.map(f => ({
          ...f,
          status: 'error'
        }));
      });
    }
  },
  clearAttachmentFile: () => {
    const { setData } = get();
    setData((state) => {
      state.filters.attachments.data = [];
      state.filters.attachments.info = initialStateFilters.attachments.info;
    });
  },
  createServiceRequest: async () => {
    const { setData, getAttachments } = get();

    setData((state) => {
      state.filters.attachments.isLoading = true;
      state.buttonDisabled = true;
    });

    const email = get().filters.email.data;

    const periodDate =
      get().formFilterInstance?.getFieldValue?.('periodDate') ?? [];

    const autoCategoryAccountSelected =
      get().formFilterInstance?.getFieldValue?.('autoCategoryId') ?? null;

    const selectedCategoryName = get().filters.autoCategories.data.find(
      (autoCategory) => autoCategory.account === autoCategoryAccountSelected,
    )?.name;

    const description =
      get().formFilterInstance?.getFieldValue?.('description');

    const completeName =
      get().formFilterInstance?.getFieldValue?.('completeName');

    const initialPeriod =
      periodDate.length > 0 ? periodDate[0].format('DD-MM-YYYY') : '';

    const finalPeriod =
      periodDate.length > 0 ? periodDate[1].format('DD-MM-YYYY') : '';

    const customerAccountCodeSelected =
      get().formFilterInstance?.getFieldValue?.('customerAccountCode') ?? null;

    const menuOrPage = get().formFilterInstance?.getFieldValue?.('menuOrPage');
    const { isFailure, isReports, isInvoiceInssuance, isInvoiceRefused, isReturnWithInvoice } = get().filters.autoCategories;
    const getFormValue = <T extends keyof IFormFilter>(field: T) =>
      get().formFilterInstance?.getFieldValue?.(field) || '';

    try {
      const params: ICreateServiceRequestParams = {
        TChamado: {
          Email: email,
          Solicitacao: '0001',
          Assunto: selectedCategoryName as string,
          AutoCategoria: autoCategoryAccountSelected,
          Descricao: description,
          TicketNumber: '',
          Attachment: getAttachments(),
        },
        TCampoExtra: {
          accountCode: customerAccountCodeSelected ?? '',
          menuOrPageFailure: isFailure ? menuOrPage : '',
          CompletName: isReports ? completeName : '',
          InicialPeriod: isReports ? initialPeriod : '',
          FinalPeriod: isReports ? finalPeriod : '',
          invoiceIssuanceLocation: isInvoiceInssuance ? getFormValue('invoiceIssuanceLocation') : '',
          saleType: isInvoiceInssuance ? getFormValue('saleType') : '',
          orderNumber: isInvoiceInssuance ? getFormValue('orderNumber') : '',
          allProductsReceived: isInvoiceInssuance ? getFormValue('allProductsReceived') : '',
          allProductsDeliveredToBox: isInvoiceInssuance
            ? getFormValue('allProductsDeliveredToBox')
            : '',
          hasOrderProductDiscrepancy: isInvoiceInssuance
            ? getFormValue('hasOrderProductDiscrepancy')
            : '',
          discrepancyDescription: isInvoiceInssuance ? getFormValue('discrepancyDescription') : '',
          responsibleName: isInvoiceInssuance
            ? getFormValue('responsibleNameWhenInvoiceIssuance')
            : isInvoiceRefused
              ? getFormValue('responsibleNameWhenInvoiceRefused')
              : '',
          siteKey: isInvoiceRefused
            ? getFormValue('siteKeyWhenInvoiceRefused')
            : isReturnWithInvoice
              ? getFormValue('siteKeyWhenReturnWithInvoice')
              : '',
          invoiceNumber: isInvoiceRefused ? getFormValue('invoiceNumber') : '',
          refusalReason: isInvoiceRefused ? getFormValue('refusalReason') : '',
          billingStore: isInvoiceRefused ? getFormValue('billingStore') : '',
          customerReturnInvoiceNumber: isReturnWithInvoice
            ? getFormValue('customerReturnInvoiceNumber')
            : '',
          customerReturnInvoiceValue: isReturnWithInvoice
            ? getFormValue('customerReturnInvoiceValue')
            : '',
          returnType: isReturnWithInvoice ? getFormValue('returnType') : '',
          returnReason: isReturnWithInvoice ? getFormValue('returnReason') : '',
          physicalProductReturned: isReturnWithInvoice ? getFormValue('physicalProductReturned') : '',
          returnLocation: isReturnWithInvoice ? getFormValue('returnLocation') : '',
          invoiceIssuingStore: isReturnWithInvoice ? getFormValue('invoiceIssuingStore') : '',
          logisticOperatorResponsibleName: isReturnWithInvoice
            ? getFormValue('logisticOperatorResponsibleName')
            : '',
          cvhInvoiceNumber: isReturnWithInvoice ? getFormValue('cvhInvoiceNumber') : '',
          cvhInvoiceValue: isReturnWithInvoice ? getFormValue('cvhInvoiceValue') : '',
        },
      };

      const { data } = await createServiceRequest(params);
      setData((state) => {
        state.filters.attachments.isLoading = false;
        state.buttonDisabled = false;
      });
      return Promise.resolve(data);
    } catch (error) {
      setData((state) => {
        state.filters.attachments.isLoading = false;
        state.buttonDisabled = false;
      });
      return Promise.reject(error);
    }
  },
}));

export default useServiceRequestsStore;
